import React, { useMemo, useState } from 'react';
import { PlatformSelector } from '../components/PlatformSelector';
import { SocialKPICards } from '../components/SocialKPICards';
import { SocialCharts } from '../components/SocialCharts';
import { allSocialData } from '../data/socialMockData';
import { formatNumber } from '../lib/format';
import { useOutletContext } from 'react-router-dom';
interface SocialContext {
  activeBrand: string;
  activeMarkets: string[];
  activeContentType: string | null;
  socialYears: string[];
}
export function SocialPage() {
  const context = useOutletContext<SocialContext>();
  const {
    activeBrand,
    activeMarkets,
    activeContentType,
    socialYears
  } = context;
  const [activeSocialPlatform, setActiveSocialPlatform] = useState<string>('Instagram');
  const filteredSocialData = useMemo(() => {
    return allSocialData.filter(item => {
      if (item.brand !== activeBrand) return false;
      if (activeMarkets.length > 0 && !activeMarkets.includes(item.market)) return false;
      if (item.platform !== activeSocialPlatform) return false;
      if (activeContentType && item.contentType !== activeContentType) return false;
      if (socialYears.length > 0 && !socialYears.includes(item.year)) return false;
      return true;
    });
  }, [activeBrand, activeMarkets, activeSocialPlatform, activeContentType, socialYears]);
  const prevYearSocialData = useMemo(() => {
    const currentYears = socialYears.length > 0 ? socialYears : ['2023', '2024', '2025', '2026'];
    const prevYears = currentYears.map(y => String(parseInt(y) - 1));
    return allSocialData.filter(item => {
      if (item.brand !== activeBrand) return false;
      if (activeMarkets.length > 0 && !activeMarkets.includes(item.market)) return false;
      if (item.platform !== activeSocialPlatform) return false;
      if (activeContentType && item.contentType !== activeContentType) return false;
      if (!prevYears.includes(item.year)) return false;
      return true;
    });
  }, [activeBrand, activeMarkets, activeSocialPlatform, activeContentType, socialYears]);
  const socialKpiData = useMemo(() => {
    const calcYoY = (current: number, previous: number): number | null => {
      if (previous === 0) return null;
      return Math.round((current - previous) / previous * 1000) / 10;
    };
    if (filteredSocialData.length === 0) {
      return {
        platform: activeSocialPlatform,
        engagementRate: '0%',
        reach: '0',
        followers: '0',
        totalViews: '0',
        avgViewsPerVideo: '0',
        yoyEngagement: null as number | null,
        yoyReach: null as number | null,
        yoyViews: null as number | null,
        yoyAvgViews: null as number | null,
        yoyFollowers: null as number | null
      };
    }
    const avgEngagement = filteredSocialData.reduce((sum, d) => sum + d.engagementRate, 0) / filteredSocialData.length;
    const totalReach = filteredSocialData.reduce((sum, d) => sum + d.reach, 0);
    const latestFollowers = Math.max(...filteredSocialData.map(d => d.followers));
    const totalViews = filteredSocialData.reduce((sum, d) => sum + d.totalViews, 0);
    const totalPosts = filteredSocialData.reduce((sum, d) => sum + d.posts, 0);
    const avgViewsPerVideo = totalPosts > 0 ? Math.floor(totalViews / totalPosts) : 0;
    let yoyEngagement: number | null = null;
    let yoyReach: number | null = null;
    let yoyViews: number | null = null;
    let yoyFollowers: number | null = null;
    let yoyAvgViews: number | null = null;
    if (prevYearSocialData.length > 0) {
      const prevAvgEngagement = prevYearSocialData.reduce((sum, d) => sum + d.engagementRate, 0) / prevYearSocialData.length;
      const prevTotalReach = prevYearSocialData.reduce((sum, d) => sum + d.reach, 0);
      const prevTotalViews = prevYearSocialData.reduce((sum, d) => sum + d.totalViews, 0);
      const prevTotalPosts = prevYearSocialData.reduce((sum, d) => sum + d.posts, 0);
      const prevAvgViewsPerVideo = prevTotalPosts > 0 ? prevTotalViews / prevTotalPosts : 0;
      const prevFollowers = Math.max(...prevYearSocialData.map(d => d.followers));
      yoyEngagement = calcYoY(avgEngagement, prevAvgEngagement);
      yoyReach = calcYoY(totalReach, prevTotalReach);
      yoyViews = calcYoY(totalViews, prevTotalViews);
      yoyAvgViews = calcYoY(avgViewsPerVideo, prevAvgViewsPerVideo);
      yoyFollowers = calcYoY(latestFollowers, prevFollowers);
    }
    return {
      platform: activeSocialPlatform,
      engagementRate: `${avgEngagement.toFixed(1)}%`,
      reach: formatNumber(totalReach),
      followers: formatNumber(latestFollowers),
      totalViews: formatNumber(totalViews),
      avgViewsPerVideo: formatNumber(avgViewsPerVideo),
      yoyEngagement,
      yoyReach,
      yoyViews,
      yoyAvgViews,
      yoyFollowers
    };
  }, [filteredSocialData, prevYearSocialData, activeSocialPlatform]);
  const socialCharts = useMemo(() => {
    const platformColorMap: Record<string, string> = {
      Instagram: '#e11d48',
      Facebook: '#3b82f6',
      TikTok: '#000000',
      LinkedIn: '#0A66C2'
    };
    const color = platformColorMap[activeSocialPlatform] || '#1e293b';
    const followersByMonth: Record<string, number> = {};
    const reachByMonth: Record<string, number> = {};
    const viewsByMonth: Record<string, number> = {};
    const engagementByMonth: Record<string, {
      sum: number;
      count: number;
    }> = {};
    filteredSocialData.forEach(item => {
      const key = `${item.year}-${item.month}`;
      if (!followersByMonth[key]) followersByMonth[key] = item.followers;
      reachByMonth[key] = (reachByMonth[key] || 0) + item.reach;
      viewsByMonth[key] = (viewsByMonth[key] || 0) + item.totalViews;
      if (!engagementByMonth[key]) {
        engagementByMonth[key] = {
          sum: 0,
          count: 0
        };
      }
      engagementByMonth[key].sum += item.engagementRate;
      engagementByMonth[key].count += 1;
    });
    const allKeys = Object.keys(followersByMonth).sort();
    const makeDataset = (label: string, dataMap: Record<string, number>) => ({
      label,
      data: allKeys.map(key => dataMap[key] || 0),
      borderColor: color,
      backgroundColor: color,
      tension: 0.4,
      borderWidth: 2
    });
    const charts: {
      title: string;
      subtitle: string;
      data: {
        labels: string[];
        datasets: any[];
      };
    }[] = [];
    const engagementTitle = activeSocialPlatform === 'TikTok' ? 'Engagement Rate by Views' : 'Engagement Rate';
    const engagementSubtitle = activeSocialPlatform === 'TikTok' ? 'Engagement rate by views over time' : 'Average engagement rate over time';
    charts.push({
      title: engagementTitle,
      subtitle: engagementSubtitle,
      data: {
        labels: allKeys,
        datasets: [{
          label: 'Engagement Rate',
          data: allKeys.map(key => {
            const d = engagementByMonth[key];
            return d ? Math.round(d.sum / d.count * 100) / 100 : 0;
          }),
          borderColor: color,
          backgroundColor: color,
          tension: 0.4,
          borderWidth: 2
        }]
      }
    });
    if (activeSocialPlatform === 'Instagram' || activeSocialPlatform === 'Facebook') {
      charts.push({
        title: 'Reach',
        subtitle: `Total reach over time`,
        data: {
          labels: allKeys,
          datasets: [makeDataset('Reach', reachByMonth)]
        }
      });
    }
    if (activeSocialPlatform === 'TikTok') {
      const avgViewsPerVideoByMonth: Record<string, number> = {};
      const postsAggByMonth: Record<string, number> = {};
      filteredSocialData.forEach(item => {
        const key = `${item.year}-${item.month}`;
        postsAggByMonth[key] = (postsAggByMonth[key] || 0) + item.posts;
      });
      allKeys.forEach(key => {
        const views = viewsByMonth[key] || 0;
        const posts = postsAggByMonth[key] || 0;
        avgViewsPerVideoByMonth[key] = posts > 0 ? Math.floor(views / posts) : 0;
      });
      charts.push({
        title: 'Average Views per Video',
        subtitle: `Average views per video over time`,
        data: {
          labels: allKeys,
          datasets: [makeDataset('Avg Views per Video', avgViewsPerVideoByMonth)]
        }
      });
    }
    charts.push({
      title: 'Followers',
      subtitle: `Follower count over time`,
      data: {
        labels: allKeys,
        datasets: [makeDataset('Followers', followersByMonth)]
      }
    });
    return charts;
  }, [filteredSocialData, activeSocialPlatform]);
  return <>
      <PlatformSelector activePlatform={activeSocialPlatform} setActivePlatform={setActiveSocialPlatform} data-id="element-203" />
      <SocialKPICards platform={socialKpiData.platform} engagementRate={socialKpiData.engagementRate} reach={socialKpiData.reach} followers={socialKpiData.followers} totalViews={socialKpiData.totalViews} avgViewsPerVideo={socialKpiData.avgViewsPerVideo} yoyEngagement={socialKpiData.yoyEngagement} yoyReach={socialKpiData.yoyReach} yoyViews={socialKpiData.yoyViews} yoyAvgViews={socialKpiData.yoyAvgViews} yoyFollowers={socialKpiData.yoyFollowers} data-id="element-204" />
      <SocialCharts charts={socialCharts} data-id="element-205" />
    </>;
}